"use client";

import { useRouter } from "next/navigation";
import { useAuth } from "@/lib/firebase/hooks/useAuth";
import { Button } from "@/components/ui/Button";
import { motion } from 'framer-motion'
import Galaxy from '@/components/reactbits/Galaxy'
import Script from 'next/script'

export default function Home() {
  const router = useRouter();
  const { user, loading } = useAuth();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "SplatX",
    "applicationCategory": "MultimediaApplication",
    "applicationSubCategory": "3D Modeling",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "creator": {
      "@type": "Organization",
      "name": "PoSTMEDIA.AI",
      "url": "https://postmedia.ai"
    },
    "description": "AI-powered 3D Gaussian Splatting platform that transforms photos into photorealistic 3D scenes with multi-format support and real-time rendering.",
    "featureList": [
      "3D Gaussian Splatting (3DGS)",
      "Multi-format support (Photos, RealityScan, COLMAP)",
      "Real-time rendering",
      "Neural rendering technology",
      "AI-powered optimization"
    ],
    "url": "https://splatx.postmedia.ai",
    "screenshot": "https://splatx.postmedia.ai/og-image.png"
  };

  // 로딩 중일 때
  if (loading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center">
        <div className="w-8 h-8 sm:w-12 sm:h-12">
          <div className="w-8 h-8 sm:w-12 sm:h-12 border-4 border-emerald-200/50 dark:border-emerald-900 border-t-emerald-600 dark:border-t-emerald-400 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* JSON-LD Structured Data for SEO */}
      <Script
        id="jsonld"
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden px-6 bg-black">
        {/* Background Effect - Fixed positioning */}
        <div className="fixed top-0 left-0 w-full h-screen pointer-events-none z-0">
          <Galaxy
            mouseRepulsion={true}
            mouseInteraction={false}
            density={3.0}
            glowIntensity={0.3}
            saturation={0.8}
            hueShift={360}
            rotationSpeed={0.05}
          />
        </div>

        <div className="relative z-10 mx-auto max-w-2xl text-center space-y-6">
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-block"
          >
            <h1
              className="text-5xl md:text-7xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-gray-400 via-gray-200 to-white"
              style={{
                textShadow: `
                  0 0 80px rgba(6, 182, 212, 0.3),
                  0 0 120px rgba(6, 182, 212, 0.2),
                  0 20px 40px rgba(0, 0, 0, 0.5)
                `,
                WebkitTextStroke: '1px rgba(255, 255, 255, 0.1)',
              }}
            >
              SplatX
            </h1>
          </motion.div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl font-semibold text-gray-300 max-w-3xl mx-auto"
          >
            AI-Powered 3D Gaussian Splatting Training Service
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-base md:text-lg font-light text-gray-400 max-w-3xl mx-auto"
          >
            Transform your images into photorealistic 3D scenes with AI-powered neural rendering and advanced 3DGS technology
          </motion.p>

          <div className="mt-10 flex items-center justify-center gap-x-6">
            {user ? (
              <Button
                href="/projects"
                variant="filled"
                arrow="right"
                className="px-6 py-2.5 text-base !bg-emerald-500"
              >
                Go to Projects
              </Button>
            ) : (
              <Button
                href="/projects"
                variant="filled"
                arrow="right"
                className="px-6 py-2.5 text-base"
              >
                Try Now
              </Button>
            )}
          </div>
        </div>

        {/* PoSTMEDIA.AI Link - Fixed at bottom */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-20"
        >
          <a
            href="https://postmedia.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-2xl md:text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-b from-gray-400 via-gray-200 to-white hover:from-gray-300 hover:via-gray-100 hover:to-white transition-all duration-300"
            style={{
              textShadow: `
                0 0 80px rgba(6, 182, 212, 0.3),
                0 0 120px rgba(6, 182, 212, 0.2),
                0 20px 40px rgba(0, 0, 0, 0.5)
              `,
              WebkitTextStroke: '0.5px rgba(255, 255, 255, 0.1)',
            }}
          >
            PoSTMEDIA.AI
          </a>
        </motion.div>
      </div>
    </>
  );
}
